import React from 'react';
import {
  FunctionField,
  Record as RaRecord,
  SimpleShowLayout,
  useTranslate,
} from 'react-admin';
import SectionTitle from '../SectionTitle';

import './styles.scss';

interface NutritionAdviceAnswersProps {
  record?: RaRecord;
}

const QUESTIONNAIRE_STEP_ID = 'answerHealthQuestionnaire';

const QUESTIONNAIRE_FIELDS = [
  'averageSleepHoursPerNight',
  'cravings',
  'dietaryHabits',
  'digestiveIssues',
  'digestiveIssuesDescription',
  'goalsToAchieve',
  'goalsToAchieveDescription',
  'height',
  'weight',
  'knownAllergiesOrIntolerances',
  'knownAllergiesOrIntolerancesDescription',
  'mealsPerDay',
  'nutritionAdviceBefore',
  'regularlyDrinkAlcohol',
  'regularlyDrinkAlcoholDescription',
  'pregnantOrTryingToBecomePregnant',
];

const capitalizeFirstLetter = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);

const prettifyValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '-';
    }

    return value.map((entry) => prettifyValue(entry)).join(', ');
  }

  if (typeof value !== 'string') {
    return String(value);
  }

  const sentenceLikeValue = value
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .trim();

  if (!sentenceLikeValue) {
    return '-';
  }

  return capitalizeFirstLetter(sentenceLikeValue);
};

const NutritionAdviceAnswers = ({ record }: NutritionAdviceAnswersProps) => {
  const translate = useTranslate();
  const questionnaireData = record?.steps?.find(
    (step: any) => step?.id === QUESTIONNAIRE_STEP_ID,
  )?.questionnaireData;

  const middleIndex = Math.ceil(QUESTIONNAIRE_FIELDS.length / 2);
  const firstColumnFields = QUESTIONNAIRE_FIELDS.slice(0, middleIndex);
  const secondColumnFields = QUESTIONNAIRE_FIELDS.slice(middleIndex);

  const renderField = (fieldName: string) => (
    <FunctionField
      key={fieldName}
      className="nutritionAdviceAnswers__textField"
      label={`nutritionAdvice.patient.${fieldName}`}
      render={() => prettifyValue(questionnaireData?.[fieldName])}
    />
  );

  return (
    <>
      <SectionTitle
        title={translate('nutritionAdvice.patientAnswers')}
        className="nutritionAdviceAnswers__title"
      />
      <div className="nutritionAdviceAnswers__column-wrapper">
        <SimpleShowLayout className="nutritionAdviceAnswers__column">
          {firstColumnFields.map(renderField)}
        </SimpleShowLayout>
        <SimpleShowLayout className="nutritionAdviceAnswers__column">
          {secondColumnFields.map(renderField)}
        </SimpleShowLayout>
      </div>
    </>
  );
};

export default NutritionAdviceAnswers;
